import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  FaArrowRight,
  FaBullhorn,
  FaChevronLeft,
  FaChevronRight,
  FaPause,
  FaPlay,
  FaTag,
} from "react-icons/fa";
import "./PromoSection.css";

const promoItems = [
  {
    type: "image",
    src: require("../images/Promo_ad1.jpg"),
    alt: "GoOurHub community discovery promotion",
    eyebrow: "Community discovery",
    title: "Find local businesses with less noise.",
    text: "Use the Hub to explore communities, discover trusted local businesses, and move faster without the clutter.",
    ctaLabel: "Explore the Hub",
    ctaTo: "/hub",
    theme: "purple",
  },
  {
    type: "image",
    src: require("../images/Promo_ad2.jpg"),
    alt: "GoOurHub HubMart marketplace promotion",
    eyebrow: "Marketplace",
    title: "Buy, sell, and donate locally in one cleaner place.",
    text: "Open HubMart to browse local listings, post faster, and skip the usual marketplace chaos.",
    ctaLabel: "Open HubMart",
    ctaTo: "/HubMart",
    theme: "gold",
  },
  {
    type: "image",
    src: require("../images/Promo_ad3.jpg"),
    alt: "GoOurHub business visibility promotion",
    eyebrow: "Visibility",
    title: "Want your business featured where people actually look?",
    text: "Partner with GoOurHub and get your brand seen in stronger local context.",
    ctaLabel: "Partner with GoOurHub",
    ctaTo: "/partner-signup",
    theme: "teal",
  },
   {
    type: "image",
    src: require("../images/Promo_ad4.jpg"),
    alt: "GoOurHub business visibility promotion",
    eyebrow: "Visibility",
    title: "Want your business featured where people actually look?",
    text: "Partner with GoOurHub and get your brand seen in stronger local context.",
    ctaLabel: "Partner with GoOurHub",
    ctaTo: "/partner-signup",
    theme: "teal",
  },
  
];

const promoDeals = [
  '🔥 Mention "GoOurHub" for selected local deals',
  "📣 Get discovered faster when your business is featured",
  "🛍️ Browse community-first listings and local favorites",
];

function PromoActionLink({ to, label }) {
  const isExternal = /^https?:\/\//i.test(to || "");

  if (isExternal) {
    return (
      <a
        href={to}
        target="_blank"
        rel="noopener noreferrer"
        className="promo-cta promo-cta--primary"
      >
        {label}
        <FaArrowRight />
      </a>
    );
  }

  return (
    <Link to={to} className="promo-cta promo-cta--primary">
      {label}
      <FaArrowRight />
    </Link>
  );
}

export default function PromoSection() {
  const [currentItem, setCurrentItem] = useState(0);
  const [currentDeal, setCurrentDeal] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const touchStartXRef = useRef(null);

  const current = promoItems[currentItem];

  const nextSlide = () => {
    setCurrentItem((prev) => (prev + 1) % promoItems.length);
  };

  const prevSlide = () => {
    setCurrentItem((prev) => (prev - 1 + promoItems.length) % promoItems.length);
  };

  const goToSlide = (index) => {
    setCurrentItem(index);
  };

  useEffect(() => {
    if (isPaused) return undefined;

    const delay = current?.type === "video" ? 6800 : 5200;
    const timer = window.setTimeout(() => {
      nextSlide();
    }, delay);

    return () => window.clearTimeout(timer);
  }, [currentItem, isPaused, current?.type]);

  useEffect(() => {
    const dealTimer = window.setInterval(() => {
      setCurrentDeal((prev) => (prev + 1) % promoDeals.length);
    }, 4200);

    return () => window.clearInterval(dealTimer);
  }, []);

  const handleTouchStart = (event) => {
    touchStartXRef.current = event.touches[0].clientX;
  };

  const handleTouchEnd = (event) => {
    if (touchStartXRef.current == null) return;

    const endX = event.changedTouches[0].clientX;
    const delta = endX - touchStartXRef.current;

    if (Math.abs(delta) > 45) {
      if (delta < 0) nextSlide();
      else prevSlide();
    }

    touchStartXRef.current = null;
  };

  const handleKeyDown = (event) => {
    if (event.key === "ArrowLeft") prevSlide();
    if (event.key === "ArrowRight") nextSlide();

    if (event.key === " ") {
      event.preventDefault();
      setIsPaused((prev) => !prev);
    }
  };

  return (
    <section
      className={`promo-container promo-container--${current.theme}`}
      aria-label="Featured promotions"
    >
      <div className="promo-card">
        <div
          className="promo-media-shell"
          tabIndex={0}
          onKeyDown={handleKeyDown}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={`promo-${currentItem}`}
              className="promo-slide"
              initial={{ opacity: 0, x: 26 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -26 }}
              transition={{ duration: 0.45, ease: "easeOut" }}
            >
              {current.type === "video" ? (
                <video
                  src={current.src}
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="promo-media"
                  aria-label={current.alt}
                />
              ) : (
                <img
                  src={current.src}
                  alt={current.alt}
                  className="promo-media"
                />
              )}

              <div className="promo-slide__overlay" />

              <div className="promo-content">
                <span className="promo-eyebrow">{current.eyebrow}</span>
                <h2 className="promo-title">{current.title}</h2>
                <p className="promo-text">{current.text}</p>

                <div className="promo-actions">
                  <PromoActionLink to={current.ctaTo} label={current.ctaLabel} />
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="promo-meta">
            <span className="promo-meta__badge">
              <FaBullhorn />
              Sponsored
            </span>

            <span className="promo-meta__count">
              {String(currentItem + 1).padStart(2, "0")} /{" "}
              {String(promoItems.length).padStart(2, "0")}
            </span>
          </div>

          <button
            type="button"
            className="promo-nav-button promo-nav-button--prev"
            onClick={prevSlide}
            aria-label="Previous promo"
          >
            <FaChevronLeft />
          </button>

          <button
            type="button"
            className="promo-nav-button promo-nav-button--next"
            onClick={nextSlide}
            aria-label="Next promo"
          >
            <FaChevronRight />
          </button>

          <button
            type="button"
            className="promo-pause-button"
            onClick={() => setIsPaused((prev) => !prev)}
            aria-label={isPaused ? "Resume autoplay" : "Pause autoplay"}
          >
            {isPaused ? <FaPlay /> : <FaPause />}
          </button>

          <div className="promo-progress" role="tablist" aria-label="Promo slides">
            {promoItems.map((item, index) => (
              <button
                key={`${item.title}-${index}`}
                type="button"
                className={`promo-dot ${index === currentItem ? "is-active" : ""}`}
                onClick={() => goToSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>

        <div className="promo-deals" aria-live="polite">
          <span className="promo-deals__label">
            <FaTag />
            Local deal
          </span>

          <AnimatePresence mode="wait" initial={false}>
            <motion.p
              key={`deal-${currentDeal}`}
              className="promo-deals__text"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.28, ease: "easeOut" }}
            >
              {promoDeals[currentDeal]}
            </motion.p>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}